import { iconSizes } from "@lazarentertainment/web-design-system/components/iconCore"
import {
  ClosedCaption,
  Maximize,
  Minimize,
  VolumeOn,
  VolumeX,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import {
  ClosedCaptionSolid,
  PauseSolid,
  PlaySolid,
} from "@lazarentertainment/web-design-system/components/iconSolid"
import { MouseEventHandler } from "react"
import styled from "styled-components"

import PlayIcon from "./icons/play.svg"
import { VideoControlButton } from "./VideoControlButton"

export const BigPlayButton = styled.button.attrs({
  "data-robot": "initial-play-button",
  children: <PlayIcon />,
})`
  appearance: none;
  border: none;
  color: white;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.accent};
  z-index: ${(props) => props.theme.zIndex.videoControls};
  border-radius: 50%;
  padding: 10px;
  box-shadow: 2px 2px 9px rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 64px;
    height: 64px;
  }
`

export type PlayPauseProps = {
  play: MouseEventHandler<HTMLButtonElement>
  pause: MouseEventHandler<HTMLButtonElement>
  isPlaying?: boolean
  size?: (typeof iconSizes)[number]
}

export const PlayPause = ({
  play,
  pause,
  isPlaying,
  size = "md",
}: PlayPauseProps) => (
  <VideoControlButton
    icon={
      isPlaying ? (
        <PauseSolid data-size={size} />
      ) : (
        <PlaySolid data-size={size} />
      )
    }
    aria-label={isPlaying ? "Pause" : "Play"}
    onClick={isPlaying ? pause : play}
    data-robot="play-pause"
  />
)

export type SubtitlesProps = {
  subtitlesVisible?: boolean
  toggleSubtitles: (subtitlesVisible: boolean) => void
}

export const Subtitles = ({
  subtitlesVisible,
  toggleSubtitles,
}: SubtitlesProps) => (
  <VideoControlButton
    icon={
      subtitlesVisible ? (
        <ClosedCaptionSolid data-size="md" />
      ) : (
        <ClosedCaption data-size="md" />
      )
    }
    aria-label={
      subtitlesVisible
        ? "Hide Captions/Subtitles"
        : "Show Captions/Subtitles"
    }
    onClick={() => toggleSubtitles(!subtitlesVisible)}
    data-robot="cc-button"
  />
)

export type MaximizeMinimizeFullscreenProps = {
  enterFullscreen?: MouseEventHandler<HTMLButtonElement>
  exitFullscreen?: MouseEventHandler<HTMLButtonElement>
  isFullscreen?: boolean
}

export const MaximizeMinimizeFullscreen = ({
  enterFullscreen,
  exitFullscreen,
  isFullscreen,
}: MaximizeMinimizeFullscreenProps) => (
  <VideoControlButton
    icon={
      isFullscreen ? (
        <Minimize data-size="md" />
      ) : (
        <Maximize data-size="md" />
      )
    }
    aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
    onClick={isFullscreen ? exitFullscreen : enterFullscreen}
    data-robot="toggle-fullscreen"
  />
)

export type MuteUnmuteProps = {
  setMuted: (isMuted: boolean) => void
  isMuted?: boolean
}

export const MuteUnmute = ({ setMuted, isMuted }: MuteUnmuteProps) => (
  <VideoControlButton
    icon={
      isMuted ? <VolumeX data-size="md" /> : <VolumeOn data-size="md" />
    }
    aria-label={isMuted ? "Unmute" : "Mute"}
    onClick={() => setMuted(!isMuted)}
    data-robot="mute-unmute"
  />
)
